/**
 * Stable Zustand selectors for chat - prevents infinite loops
 * 
 * Key principles:
 * 1. Never create new arrays/objects in selectors
 * 2. Return primitive values or stable references only
 * 3. Derive complex data in components with useMemo
 */

import { useCallback, useMemo, useRef } from 'react';
import { useChatStore } from '../../stores/chatStore';
import { useShallow } from 'zustand/react/shallow';

// Stable empty fallbacks to avoid new references and undefined access
const EMPTY_ARRAY: readonly string[] = Object.freeze([]);
const EMPTY_OBJECT: Readonly<Record<string, any>> = Object.freeze({});

/**
 * Get message IDs for a conversation (stable)
 */
export function useMessageIds(conversationId?: string | null) {
  // Guard against undefined conversation IDs to avoid runtime errors
  const key = conversationId ?? '';
  return useChatStore((state) => {
    try {
      const map = (state && (state as any).messageIdsByConvRef) || {};
      const ids = (map as Record<string, unknown>)[key];
      return Array.isArray(ids) ? (ids as string[]) : (EMPTY_ARRAY as string[]);
    } catch {
      return EMPTY_ARRAY as string[];
    }
  });
}

/**
 * Get all messages as a stable reference
 */
export function useMessagesById() {
  return useChatStore((state) => {
    try {
      const map = (state && (state as any).messagesById) as Record<string, any> | undefined;
      return map && typeof map === 'object' ? map : (EMPTY_OBJECT as Record<string, any>);
    } catch {
      return EMPTY_OBJECT as Record<string, any>;
    }
  });
}

/**
 * Get hydration status (primitive, always stable)
 */
export function useHydrationStatus(conversationId?: string | null) {
  const key = conversationId ?? '';
  return useChatStore((state) => {
    try {
      const hydration = (state as any)?.hydration || {};
      return hydration[key] || 'idle';
    } catch {
      return 'idle';
    }
  });
}

/**
 * Get current stream status (returns stable object)
 */
export function useStreamStatus() {
  // Use useShallow to prevent re-renders when object reference changes
  return useChatStore(
    useShallow((state) => ({
      isStreaming: state.currentStream?.status === 'streaming',
      conversationRef: state.currentStream?.conversationRef,
      error: state.currentStream?.error
    }))
  );
}

/**
 * Combined hook for chat messages with proper memoization
 */
export function useStableMessages(conversationId?: string | null) {
  // Always call inner hooks to respect the Rules of Hooks across renders
  const key = conversationId ?? '';
  // Get stable message IDs (safe: always returns an array)
  const messageIds = useMessageIds(key);
  // Get messages map (stable reference)
  const messagesById = useMessagesById();

  // Derive messages array in component (memoized, with extra guards)
  const messages = useMemo(() => {
    try {
      const ids = Array.isArray(messageIds) ? messageIds : [];
      const map = (messagesById && typeof messagesById === 'object') ? messagesById : {};
      // Filter out any undefined entries defensively
      return ids.map((id) => (map as Record<string, any>)[id]).filter((m: any) => !!m);
    } catch {
      return [] as any[];
    }
  }, [messageIds, messagesById]);

  return messages;
}

/**
 * Get functions without subscribing (prevents re-renders)
 * Returns stable functions that won't cause re-renders
 */
export function useChatActions() {
  // Use useMemo with empty deps to create stable action references
  return useMemo(() => {
    // Get the store state once
    const store = useChatStore.getState();
    
    // Return stable function references
    // These functions are defined on the store and won't be undefined
    return {
      addUserMessageDraft: (conversationRef: string, content: string) => {
        return useChatStore.getState().addUserMessageDraft(conversationRef, content);
      },
      startStream: (
        conversationRef: string,
        opts?: { threadId?: string; parentMessageId?: string | null; sideStream?: boolean; pendingUserText?: string }
      ) => {
        return useChatStore.getState().startStream(conversationRef, opts);
      },
      appendToStream: (token: string) => {
        const state = useChatStore.getState();
        if (state.appendToStream) {
          state.appendToStream(token);
        }
      },
      completeStream: () => {
        const state = useChatStore.getState();
        if (state.completeStream) {
          state.completeStream();
        }
      },
      setStreamError: (error: string) => {
        const state = useChatStore.getState();
        if (state.setStreamError) {
          state.setStreamError(error);
        }
      },
      hydrateConversation: (
        conversationRef: string,
        messages: any[],
        hasMore: boolean,
        nextBeforeId?: number
      ) => {
        return useChatStore.getState().hydrateConversation(
          conversationRef,
          messages,
          hasMore,
          nextBeforeId
        );
      },
      setHydrationStatus: (conversationRef: string, status: any) => {
        return useChatStore.getState().setHydrationStatus(conversationRef, status);
      },
    };
  }, []); // Empty deps - functions never change
}
